import { isProductionEnvironment, isTrainingSandbox } from "@remi/environment";
import { NavigationItem } from "@remi/types/navigation";

import { hasCapability, type User } from "../base";
import { adminTools } from "./adminTools";
import { bidTable } from "./bidTable";
import { organizationSettings } from "./organization";
import { projectTable } from "./project";
import { ticket } from "./ticket";

const NAVIGATION_LIST_ITEMS: NavigationItem[] = [
  NavigationItem.PRODUCTION_SCHEDULE,
  NavigationItem.TODAYS_ACTIONS,
  NavigationItem.PROJECT_MILESTONES,
  NavigationItem.TICKETS,
  NavigationItem.MY_QUEUE,
  NavigationItem.BIDS,
  NavigationItem.PROJECTS,
  NavigationItem.TASKS,
  NavigationItem.ESCALATIONS,
  NavigationItem.CONTRACTORS,
  NavigationItem.DISPATCH,
  NavigationItem.FINANCIAL_CONSOLE,
  NavigationItem.REMITTANCE,
  NavigationItem.INSURANCE,
  NavigationItem.MY_ORGANIZATION,
  NavigationItem.ADMIN,
  NavigationItem.TRAINING_CONTRACTORS,
  NavigationItem.DEVELOPER_PORTAL,
  NavigationItem.DEVTOOLS,
];

export const navigation = {
  read: (user: User, navigationItem: NavigationItem): boolean => {
    switch (navigationItem) {
      case "BIDS":
        return bidTable.read(user);
      case "PROJECTS":
      case "PROJECTS_V2":
        return projectTable.read(user);
      case "ADMIN":
        return adminTools.read(user);
      case "DEVTOOLS":
        return adminTools.read(user) && !isProductionEnvironment();
      case "TRAINING_CONTRACTORS":
        return isTrainingSandbox() && adminTools.trainingAdminTools.manage(user);
      case "FINANCIAL_CONSOLE":
        return hasCapability(user, "navigation.read.financialConsole");
      case "REMITTANCE":
        // Contractors view their own AP here too. `remittance.read` covers
        // REMI_USER+; `remittance.read.own` covers CONTRACTOR_ADMIN. The
        // tRPC query enforces org-scoping per-call. Carved out from
        // `bill.read.*` so the remittance surface owns its own gate
        // independent of bill-CRUD access.
        return hasCapability(user, "remittance.read") || hasCapability(user, "remittance.read.own");
      case "TASKS":
      case "TASKS_V2":
      case "VIEW_MY_TASKS":
        return hasCapability(user, "navigation.read.tasks");
      case "TODAYS_ACTIONS":
      case "PRODUCTION_SCHEDULE":
        return hasCapability(user, "navigation.read.todaysActions");
      case "TRAINING":
        return isTrainingSandbox() && organizationSettings.update(user);
      case "DEVELOPER_PORTAL":
        return hasCapability(user, "navigation.read.developerPortal");
      case "PROJECT_MILESTONES":
        return hasCapability(user, "navigation.read.projectMilestones");
      case "TICKETS":
      case "MY_QUEUE":
        return ticket.list(user);
      case "CONTRACTORS":
        return hasCapability(user, "navigation.read.contractors");
      case "CONTRACTOR_DASHBOARD":
        // The dash page's cross-organization plate read requires contractorProfile.read.all; contractors read their own.
        return hasCapability(user, "contractorProfile.read.all") || hasCapability(user, "navigation.read.performance");
      case "DISPATCH":
        return hasCapability(user, "navigation.read.dispatch");
      case "MY_ORGANIZATION":
        return organizationSettings.update(user);
      case "ESCALATIONS":
      case "ESCALATION_OVERVIEW":
      case "ESCALATION_HUB":
      case "ESCALATION_TABLE":
        return hasCapability(user, "navigation.read.escalations");
      case "ACTIONABLES":
        return hasCapability(user, "navigation.read.actionables");
      case "LIVE_PHOTO_REVIEW":
      case "VIEW_TODAYS_ACTIONS":
      case "VIEW_PRODUCTION_SCHEDULE":
      case "VIEW_OPERATIONS_COORDINATOR":
      case "VIEW_STANDARD_QUOTING":
      case "VIEW_INSURANCE_QUOTING":
      case "VIEW_UPSELL_QUOTING":
      case "VIEW_LIVE_QUOTING":
      case "VIEW_QUOTE_REVIEW":
      case "VIEW_INTRO_CALL":
      case "VIEW_PRE_PRODUCTION":
      case "VIEW_CONTRACTOR_PRODUCTION":
      case "VIEW_FINAL_CHECK_OFF":
      case "VIEW_FINANCE":
      case "VIEW_PERMITTING":
      case "VIEW_CHANGE_ORDERS":
      case "VIEW_POST_INSTALL_ISSUES":
      case "VIEW_BLOCKING_TASKS":
      case "VIEW_MY_BLOCKING_TASKS":
      case "VIEW_UPSELLS":
      case "VIEW_REVIEW_FINANCIAL_ADJUSTMENTS":
      case "VIEW_CONTRACTOR_DOCUMENTS":
      case "VIEW_REVIEW_BIDS":
      case "VIEW_REMI_LABOR":
      case "VIEW_ENTERPRISE_MANAGEMENT":
      case "VIEW_MY_WESTHILL_TASKS":
      case "VIEW_UNASSIGNED_WESTHILL_TASKS":
      case "VIEW_TRAVELERS_CLEANUP":
      case "VIEW_MY_OPENLY_TASKS":
      case "VIEW_UNASSIGNED_OPENLY_TASKS":
      case "VIEW_CORRECTIONS":
      case "VIEW_PARTNER_SUCCESS_TASKS":
      case "VIEW_APPEASEMENTS":
      case "VIEW_VENDOR_CREDITS":
      case "VIEW_CONCESSION_APPROVALS":
      case "VIEW_ITEL_TASKS":
        return hasCapability(user, "navigation.read.views");
      case "VIEW_INSURANCE_MY_ASSIGNMENTS":
      case "VIEW_INSURANCE_ORG_ASSIGNMENTS":
      case "VIEW_INSURANCE_MY_ASSIGNMENTS_PENDING_ESTIMATE":
      case "VIEW_INSURANCE_MY_ASSIGNMENTS_CANCELED":
      case "VIEW_INSURANCE_MY_ASSIGNMENTS_APPROVED":
      case "VIEW_INSURANCE_MY_ASSIGNMENTS_CORRECTIONS_REQUESTED":
      case "VIEW_INSURANCE_MY_ASSIGNMENTS_PENDING_APPROVAL":
      case "VIEW_INSURANCE_MY_ASSIGNMENTS_COMP_ESTIMATES":
      case "VIEW_INSURANCE_MY_ASSIGNMENTS_JOB_SOLD":
      case "VIEW_INSURANCE_MY_ASSIGNMENTS_JOB_NOT_SOLD":
      case "VIEW_INSURANCE_MY_ASSIGNMENTS_JOB_STARTED":
      case "VIEW_INSURANCE_MY_ASSIGNMENTS_JOB_COMPLETED":
        return hasCapability(user, "navigation.read.insuranceMyAssignments");
      case "INSURANCE":
      case "VIEW_INSURANCE_ALL_ASSIGNMENTS":
      case "VIEW_ESTIMATING":
      case "VIEW_ESTIMATING_APPROVALS":
      case "VIEW_MY_ESTIMATING_TASKS":
        return hasCapability(user, "navigation.read.insuranceAll");
      case "VIEW_1099_ESTIMATING":
        return hasCapability(user, "navigation.read.estimating");
      default: {
        const _exhaustive: never = navigationItem;
        return false;
      }
    }
  },

  hasAnyNavigationListItems: (user: User): boolean => {
    return NAVIGATION_LIST_ITEMS.some((item) => navigation.read(user, item));
  },
};
